import type { ReactNode } from 'react';
import type { Action, PlainAction } from '@components/types';
import type { Breakpoint, Filter } from '@pages/User/components/table/shared/types/table.types';
import type { RankingInfo } from '@tanstack/match-sorter-utils';
import type { QueryStatus } from '@tanstack/react-query';
import type {
  ColumnFiltersState,
  ColumnPinningState,
  FilterFn,
  RowData,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';

import { TABLE_CONSTANTS } from '../shared/constants';

import { TableContainer } from './components/TableContainer';
import { useDataTable } from './hooks';

interface ReactTableProps {
  actions?: Action[];
  columns: any[];
  columnVisibility?: VisibilityState;
  columnPinning?: ColumnPinningState;
  createEntityAction?: PlainAction;
  enableColumnFilters?: boolean;
  entityName: string;
  expandableData?: any[];
  filteredData: any[];
  filtersBarWrapperClassName?: string;
  initialFilters?: ColumnFiltersState;
  initialSorting?: SortingState;
  isEmpty?: boolean;
  isFetching?: boolean;
  isLoading?: boolean;
  pageSize?: number;
  renderCustomElements?: (
    isGroupByParent: boolean,
    setIsGroupByParent: (isGroupByParent: boolean) => void,
  ) => () => ReactNode;
  status?: QueryStatus;
  tableClasses?: string;
}

declare module '@tanstack/react-table' {
  // eslint-disable-next-line unused-imports/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    tooltip?: boolean;
    filter?: Partial<Filter> & {
      order?: number;
      isSingleSelection?: boolean;
    };
    dataCellClassName?: string;
    headingCellClassName?: string;
    headingLinkClassName?: string;
    isStandaloneDataCell?: boolean;
    showAboveBreakpoint?: Breakpoint;
  }
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

export const ReactTable = ({
  actions = [],
  columnPinning = {},
  columns,
  columnVisibility = {},
  createEntityAction,
  enableColumnFilters = true,
  entityName,
  expandableData = [],
  filteredData,
  filtersBarWrapperClassName,
  initialFilters,
  initialSorting,
  isEmpty,
  isFetching = false,
  isLoading = false,
  pageSize = TABLE_CONSTANTS.DEFAULT_PAGE_SIZE,
  renderCustomElements,
  status,
  tableClasses,
}: ReactTableProps) => {
  const tableApi = useDataTable({
    columnPinning: isEmpty ? undefined : columnPinning,
    columns,
    columnVisibility,
    data: filteredData,
    createEntityAction,
    enableColumnFilters,
    entityName,
    expandableData,
    initialFilters,
    initialSorting,
    isEmpty,
    isFetching,
    isLoading,
    pageSize,
    renderCustomElements,
    status,
  });

  const isStatusLoading = !['error', 'success'].includes(status ?? '');
  const isTableInitialized = tableApi.meta.isInitialized;
  const isTableContainerLoading = isLoading || isFetching || !isTableInitialized || isStatusLoading;

  return (
    <TableContainer
      actions={actions}
      className={tableClasses}
      enableColumnFilters={enableColumnFilters}
      enableColumnVisibility={true}
      enableGlobalSearch={true}
      enablePagination={true}
      entityName={entityName}
      filtersBarWrapperClassName={filtersBarWrapperClassName}
      isEmpty={isEmpty}
      isLoading={isTableContainerLoading}
      pageSize={pageSize}
      renderCustomElements={renderCustomElements}
      tableApi={tableApi}
    />
  );
};
